import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import {
  SharedContentGridLayout,
  SharedGridStyle,
} from "features/CenterContent/shared/MainContentGrid";
import { ReserveVehicleInfo } from "features/CenterContent/VehicleDetails/ReserveAlternativeTransportDetails/ReserveVehicleInfo";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import { useParams } from "react-router-dom";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { Text, VStack } from "shared/components/Spor";
import { reserveAlternativeTransportSchema } from "shared/types/alternativeTransport";
import styled from "styled-components";
import * as z from "zod";
import TabsCenter from "features/CenterContent/shared/Tabs/TabsCenter";

const ContentContainer = styled.div`
  ${SharedContentGridLayout};
  grid-template-columns: 2fr 2fr;
  grid-template-rows: auto 1fr;

  & > * {
    ${SharedGridStyle};
  }

  & > *:nth-child(2) {
    grid-column: 1 / 1;
  }
`;

const ErrorContainer = styled.div`
  ${SharedContentGridLayout};
  ${SharedGridStyle};
  box-sizing: border-box;
`;

export const DetailsLoader = () => (
  <ContentContainer>
    <div style={{ gridColumn: "1 / span 2", alignItems: "center" }}>
      <SkeletonLoader skeletonType="trainStops" />
    </div>
    <div>
      <SkeletonLoader skeletonType="list" />
    </div>
  </ContentContainer>
);

const ReserveAlternativeTransportDetails = () => {
  const { vehicleUuid } = useParams();

  const { status, data: reserveVehicle } = useQuery({
    queryKey: ["reserveAltTransport", vehicleUuid],
    refetchInterval: 30000,
    queryFn: async ({ signal }) =>
      axiosClient
        .get<
          z.infer<typeof reserveAlternativeTransportSchema>
        >(`${getBackendUrl()}/alternativeTransports/reserve/${vehicleUuid}`, { signal })
        .then((res) => reserveAlternativeTransportSchema.parse(res.data)),
  });

  const getContent = () => {
    switch (status) {
      case "pending":
        return <DetailsLoader />;
      case "success":
        return reserveVehicle ? (
          <ContentContainer>
            <ReserveVehicleInfo reserveAlternativeTransport={reserveVehicle} />
            <VStack style={{ width: "100%", padding: 0 }}>
              {reserveVehicle.notes.length > 0 && (
                <div style={{ width: "100%", padding: "0.75rem" }}>
                  <ExpandableInfoMessage
                    semantics="info"
                    title="Info fra Brudd"
                  >
                    {reserveVehicle.notes.map((note) => (
                      <Text key={note}>{note}</Text>
                    ))}
                  </ExpandableInfoMessage>
                </div>
              )}
              <OperationalLog
                vehicleId={reserveVehicle.id}
                logs={reserveVehicle.logs ?? []}
              />
            </VStack>
          </ContentContainer>
        ) : (
          <ErrorContainer>
            <NoDataMessage reason="Fant ingen detaljer om bussen" />
          </ErrorContainer>
        );
      case "error":
        return (
          <ErrorContainer>
            <FailureMessage />
          </ErrorContainer>
        );
    }
  };

  return (
    <>
      <TabsCenter isReserveBus />
      {getContent()}
    </>
  );
};

export default withErrorBoundary(ReserveAlternativeTransportDetails);
